import commonStyles from 'src/common/common.module.scss';
import { FC, useEffect } from 'react';
import { Text, Link } from 'src/shared/components/typography';
import { Checkbox, Col, Modal, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Input, message, ModalProps, Spin } from 'antd';
import { useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { isEmail, isNullOrWhiteSpaces } from 'src/shared/utils/strings';
import { ContactUserRole } from 'src/api/contact/types';
import container from 'src/config/inversify-config';
import { BugReportFormApiService } from 'src/api/bug-report/bug-report-api';
import { BrowserEnvironment, captureEnvironment } from 'src/utils/browser-env';
import { BugReportSource } from 'src/api/bug-report/types';

const { Paragraph } = Typography;

export interface BugReportModalProps extends ModalProps {
  role: ContactUserRole;
  onPrivacyPolicyClick?: () => void;
  initialFirstName?: string;
  initialLastName?: string;
  initialEmail?: string;
  initialComment?: string;
  initialSource?: BugReportSource;
}

const BugReportModal: FC<BugReportModalProps> = ({
  role,
  onPrivacyPolicyClick,
  initialFirstName,
  initialLastName,
  initialEmail,
  initialComment,
  initialSource,
  ...modalProps
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);
  const [comment, setComment] = useState<string>(initialComment ?? '');
  const [email, setEmail] = useState<string>(initialEmail ?? '');
  const [firstName, setName] = useState<string>(initialFirstName ?? '');
  const [lastName, setLastName] = useState<string>(initialLastName ?? '');
  const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState(role !== ContactUserRole.Anonymous);
  const [envInfo, setEnvInfo] = useState<BrowserEnvironment | null>(null);

  const bugReportFormApi = container.get<BugReportFormApiService>(BugReportFormApiService);

  const onSave = async (): Promise<boolean> => {
    if (!privacyPolicyChecked) {
      message.error(t('Debes aceptar el tratamiento de tus datos'));
      return false;
    }
    setLoading(true);
    try {
      const response = await bugReportFormApi.createBugReport({
        role: role,
        comment,
        userInfo: {
          email: isNullOrWhiteSpaces(email) ? undefined : email,
          firstName: firstName.trim() !== '' ? firstName : undefined,
          lastName: lastName.trim() !== '' ? lastName : undefined
        },
        source: initialSource,
        context: envInfo ?? undefined
      });

      if (response.isSuccess) {
        message.success(t('Reporte enviado con éxito.'));
        return true;
      } else {
        message.error(response.error != null ? response.error : t('Ha ocurrido un error enviando el reporte.'));
        return false;
      }
    } catch (e) {
      console.error('BugReportModal > onSave', { e });
      message.error({
        content: t('Error inesperado')
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const [validationStatus, setValidationStatus] = useState<{ [key: string]: string }>({});
  const [validateOnChange, setValidateOnChange] = useState<boolean>(false);

  const validate = (): { ok: boolean; result: { [key: string]: string } } => {
    const result: { [key: string]: string } = {};
    let ok = true;

    if (isNullOrWhiteSpaces(comment)) {
      result['comment'] = t('Valor requerido');
      ok = false;
    }

    if (role === ContactUserRole.Anonymous && !isEmail(email)) {
      result['email'] = t('Correo electrónico inválido');
      ok = false;
    }

    if (!privacyPolicyChecked) {
      result['privacyChk'] = t('Debes aceptar el tratamiento de tus datos');
      ok = false;
    }

    return { ok, result };
  };

  useEffect(
    () => {
      if (!validateOnChange) return;
      const validation = validate();
      setValidationStatus(validation.result);
      setValidateOnChange(false);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [validateOnChange]
  );

  useEffect(() => {
    setLoading(false);
    setComment(initialComment ?? '');
    setEmail(initialEmail ?? '');
    setName(initialFirstName ?? '');
    setLastName(initialLastName ?? '');
    setPrivacyPolicyChecked(role !== ContactUserRole.Anonymous);
    setValidationStatus({});
    setValidateOnChange(false);
    if (modalProps?.open) {
      setEnvInfo(captureEnvironment());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalProps?.open, role]);

  return (
    <Modal
      width={674}
      className={`${commonStyles.defaultModal} ${commonStyles.flexFooterModal}`}
      rootClassName={commonStyles.defaultModalRoot}
      title={<span style={{ fontSize: 30 }}>{t('Reportar una incidencia')}</span>}
      cancelText={t('Cancela')}
      destroyOnHidden={true}
      okText={t('Envía')}
      okButtonProps={{
        disabled: loading,
        shape: 'round',
        className: commonStyles.commonButton,
        style: { marginLeft: 16 }
      }}
      cancelButtonProps={{ disabled: loading, shape: 'round', className: commonStyles.commonButton }}
      mask={{ closable: !loading }}
      closable={!loading}
      closeIcon={<IconX stroke={2} color="#1A1C21" />}
      centered
      {...modalProps}
      onOk={async (e) => {
        setValidateOnChange(true);
        const isValid = validate().ok;
        if (isValid) {
          const saved = await onSave();
          if (saved) {
            modalProps.onOk!(e);
            setComment('');
            setValidationStatus({});
            setValidateOnChange(false);
          }
        }
      }}
      onCancel={async (e) => {
        modalProps.onCancel!(e);
        setValidationStatus({});
        setValidateOnChange(false);
      }}
    >
      <Spin spinning={loading} style={{ marginTop: 30, marginBottom: 48 }}>
        <div style={{ marginBottom: 60 }}>
          <Typography>
            <Paragraph style={{ marginBottom: 30, marginTop: 20 }}>
              <Text fontSize={16}>
                {t(
                  'Si has encontrado algún error o problema técnico, por favor descríbelo aquí. Tu ayuda es fundamental para mejorar nuestra plataforma.'
                )}
              </Text>
            </Paragraph>

            {role === ContactUserRole.Anonymous && (
              <Row gutter={[26, 48]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                  <Paragraph style={{ marginBottom: 5 }}>
                    <Text fontSize={18}>{t('Nombre')}</Text>&nbsp;
                    <Text fontSize={18} color="#7F88A1" fontFamily="OrkneyRegular">
                      {t('(opcional)')}
                    </Text>
                  </Paragraph>
                  <Input
                    className="j2l-big-input"
                    disabled={loading}
                    size="large"
                    placeholder={t('Escribe tu nombre')}
                    value={firstName}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                  <Paragraph style={{ marginBottom: 5 }}>
                    <Text fontSize={18}>{t('Apellidos')}</Text>&nbsp;
                    <Text fontSize={18} color="#7F88A1" fontFamily="OrkneyRegular">
                      {t('(opcional)')}
                    </Text>
                  </Paragraph>
                  <Input
                    className="j2l-big-input"
                    disabled={loading}
                    size="large"
                    placeholder={t('Escribe tus apellidos')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Col>
              </Row>
            )}

            {role === ContactUserRole.Anonymous && (
              <div style={{ marginTop: 48 }}>
                <Paragraph style={{ marginBottom: 5 }}>
                  <Text fontSize={18}>{t('Correo electrónico')}</Text>
                </Paragraph>
                <Input
                  className="j2l-big-input"
                  disabled={loading}
                  size="large"
                  placeholder={t('Escribe tu correo electrónico')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  status={!isNullOrWhiteSpaces(validationStatus['email']) ? 'error' : undefined}
                />
                {!isNullOrWhiteSpaces(validationStatus['email']) && (
                  <div style={{ marginTop: 8 }}>
                    <Text fontSize={13} color="#ff4d4f">
                      {validationStatus['email']}
                    </Text>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 48 }}>
              <Paragraph style={{ marginBottom: 5 }}>
                <Text fontSize={18}>{t('Descripción del error')}</Text>
              </Paragraph>
              <Input.TextArea
                disabled={loading}
                size="large"
                placeholder={t('Describe detalladamente el problema que has encontrado')}
                maxLength={500}
                style={{ height: 130 }}
                showCount
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                status={!isNullOrWhiteSpaces(validationStatus['comment']) ? 'error' : undefined}
              />
              {!isNullOrWhiteSpaces(validationStatus['comment']) && (
                <div style={{ marginTop: 8 }}>
                  <Text fontSize={13} color="#ff4d4f">
                    {validationStatus['comment']}
                  </Text>
                </div>
              )}
            </div>
            {role === ContactUserRole.Anonymous && (
              <div style={{ marginTop: 48 }}>
                <div>
                  <Checkbox style={{ marginRight: 10 }} checked={privacyPolicyChecked} onChange={(e) => setPrivacyPolicyChecked(e.target.checked)} />
                  <Text fontSize={14}>{t('Jobs2live usará tu email y nombre para contactar contigo. Consulta la')}</Text>&nbsp;
                  <Link
                    underline
                    fontSize={14}
                    href={onPrivacyPolicyClick == null ? '/website/privacy-policy' : undefined}
                    onClick={
                      onPrivacyPolicyClick
                        ? (e) => {
                            e.preventDefault();
                            onPrivacyPolicyClick();
                            return false;
                          }
                        : undefined
                    }
                  >
                    {t('política de privacidad')}
                  </Link>
                </div>
                {!isNullOrWhiteSpaces(validationStatus['privacyChk']) && (
                  <div style={{ marginTop: 5 }}>
                    <Text fontSize={14} color="#ff4d4f">
                      {validationStatus['privacyChk']}
                    </Text>
                  </div>
                )}
              </div>
            )}
          </Typography>
        </div>
      </Spin>
    </Modal>
  );
};

export default BugReportModal;
